import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { inventoryApi } from '../api/inventory-api-instance';
import { ForbiddenError, NotFoundError } from '../api/errors';
import { useAuth } from '../auth/AuthContext';
import { CustomerContactsTab } from '../features/contacts/CustomerContactsTab';
import { ErrorState } from '../components/ErrorState';
import { PageHeader } from '../components/PageHeader';
import { PageLoadingState } from '../components/PageLoadingState';
import { SearchableSelect, type SearchableSelectOption } from '../components/SearchableSelect';
import { subscriptionFeatures, type Customer, type SubscriptionFeature } from '../domain/inventory';
import { EditSubscriptionDrawer } from '../features/entitlements/EditSubscriptionDrawer';
import { NewContractTermDialog } from '../features/entitlements/NewContractTermDialog';
import { subscriptionTermStatus, termProgress } from '../features/entitlements/term-status';
import {
  buildCustomerInsights,
  compareCameraSystems,
  customerLocationSummary,
  groupCustomerSiteInsights,
  siteGenerationLabels,
  siteGenerations,
  type CustomerSiteInsight,
  type GenerationComponentCounts,
  type SiteGeneration,
} from '../features/customers/customer-insights';
import { useOpenRequestCountsBySite } from '../features/field-work/open-requests-by-site';
import { formatNoteDate, type SiteStatusSummary } from '../domain/siteNote';
import { useSitePenCounts, useSiteStatusSummaries } from '../features/site-status/site-status-data';
import { formatPenCountsDate, type SitePenCounts } from '../domain/sitePenCounts';
import { SiteContactSummary } from '../features/site-contact/SiteContactSummary';
import { SiteStatusTable } from '../features/site-status/SiteStatusTable';
import { siteContactSearchText } from '../domain/siteContact';
import { humanizeIdentifier } from '../lib/formatting';
import { siteLabel } from '../lib/site-label';

type Tab = 'overview' | 'sites' | 'contacts' | 'entitlements';

const REAL_FEATURES = subscriptionFeatures.filter(
  (feature): feature is Exclude<SubscriptionFeature, 'FEATURE_UNSPECIFIED'> =>
    feature !== 'FEATURE_UNSPECIFIED',
);

const TERM_STATUS_LABELS: Record<ReturnType<typeof subscriptionTermStatus>, string> = {
  active: 'Active',
  'renewing-soon': 'Renews soon',
  ended: 'Term ended',
};

function formatDate(value: string | undefined): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not recorded' : date.toISOString().slice(0, 10);
}

function customerType(customer: Customer): string {
  return customer.customerType ?? 'CUSTOMER';
}

function customerInventoryHref(
  customerId: string,
  scope?: 'assigned' | 'on-site' | 'away-from-assignment',
  attention = false,
): string {
  const query = new URLSearchParams({ customerId });
  if (scope) query.set('customerScope', scope);
  if (attention) query.set('attention', 'true');
  return `/inventory?${query.toString()}`;
}

function componentSummary(
  generation: 'gen1' | 'gen2',
  components: GenerationComponentCounts,
): string {
  const controlPowerCount =
    generation === 'gen1' ? components.unitControllers : components.powerboxes;
  if (components.cameras + components.winches + controlPowerCount === 0) return '—';

  const entries = [
    `${components.cameras.toLocaleString()} camera`,
    `${components.winches.toLocaleString()} winch`,
    generation === 'gen1'
      ? `${controlPowerCount.toLocaleString()} unit controller`
      : `${controlPowerCount.toLocaleString()} powerbox`,
  ];
  return entries.join(' · ');
}

const SITE_GENERATION_ORDER: Record<SiteGeneration, number> = {
  gen1: 0,
  gen2: 1,
  hybrid: 2,
  unclassified: 3,
};

function CustomerSiteCard({
  insight,
  openRequests,
  status,
  pens,
}: {
  insight: CustomerSiteInsight;
  openRequests: number;
  status: SiteStatusSummary | undefined;
  pens: SitePenCounts | undefined;
}) {
  const sitePath = `/customers/${encodeURIComponent(insight.site.customerId)}/sites/${encodeURIComponent(insight.site.id)}`;
  return (
    <article className={`customer-site-card customer-site-${insight.generation}`}>
      <div className="customer-site-heading">
        <div>
          <h5>
            <Link to={sitePath}>{siteLabel(insight.site)}</Link>
          </h5>
          {insight.site.officialSiteId ? <p>Official ID {insight.site.officialSiteId}</p> : null}
        </div>
        <span className={`generation-badge generation-${insight.generation}`}>
          {siteGenerationLabels[insight.generation]}
        </span>
      </div>

      <dl className="customer-site-metrics">
        <div>
          <dt>Ordered</dt>
          <dd>{insight.site.orderedSystems?.toLocaleString() ?? '—'}</dd>
        </div>
        <div>
          <dt>Systems</dt>
          <dd title="Camera systems assigned to the site, and how many are active">
            {insight.cameraSystems.assigned.toLocaleString()}
            <small className="customer-site-metric-note">
              {insight.cameraSystems.active.toLocaleString()} active
            </small>
          </dd>
        </div>
        <div>
          <dt>Pens with fish</dt>
          <dd
            title={
              pens
                ? `From the farm's data on ${formatPenCountsDate(pens.date)}: pens with fish, and pens our systems sent data from`
                : 'No farm data for this customer'
            }
          >
            {pens ? pens.pensWithFish.toLocaleString() : '—'}
            {pens ? (
              <small className="customer-site-metric-note">
                {pens.pensWithData.toLocaleString()} with data
              </small>
            ) : null}
          </dd>
        </div>
        <div>
          <dt>Assigned</dt>
          <dd>{insight.assignedItems.toLocaleString()}</dd>
        </div>
        <div>
          <dt>On site</dt>
          <dd>{insight.locatedItems.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Attention</dt>
          <dd>{insight.needsAttention.toLocaleString()}</dd>
        </div>
        <div>
          <dt>Open requests</dt>
          <dd>
            <Link to={sitePath}>{openRequests.toLocaleString()}</Link>
          </dd>
        </div>
      </dl>

      {insight.gen1Items > 0 || insight.gen2Items > 0 ? (
        <div className="customer-site-components" aria-label="Generation components">
          {insight.gen1Items > 0 ? (
            <span>
              <strong>Gen 1</strong> {componentSummary('gen1', insight.gen1Components)}
            </span>
          ) : null}
          {insight.gen2Items > 0 ? (
            <span>
              <strong>Gen 2</strong> {componentSummary('gen2', insight.gen2Components)}
            </span>
          ) : null}
        </div>
      ) : null}

      {status?.latest || status?.lastContact ? (
        <p className="customer-site-status">
          {status.lastContact ? (
            <span>Last contact {formatNoteDate(status.lastContact.createTime)}</span>
          ) : null}
          {status.latest ? (
            <span className="customer-site-status-note" title={status.latest.text}>
              {status.latest.text}
            </span>
          ) : null}
        </p>
      ) : null}

      <SiteContactSummary site={insight.site} />

      <div className="customer-site-links">
        <Link to={`/inventory?assignedSiteId=${encodeURIComponent(insight.site.id)}`}>
          Assigned
        </Link>
        <Link to={`/inventory?locationSiteId=${encodeURIComponent(insight.site.id)}`}>On site</Link>
        <Link
          to={`/inventory?assignedSiteId=${encodeURIComponent(insight.site.id)}&attention=true`}
        >
          Attention
        </Link>
      </div>
    </article>
  );
}

export function CustomerDetailPage() {
  const openRequestsBySite = useOpenRequestCountsBySite();
  const { customerId = '' } = useParams();
  const siteStatus = useSiteStatusSummaries(customerId);
  const penCounts = useSitePenCounts(customerId);
  const { canMutate } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeTab: Tab =
    params.get('tab') === 'sites' ||
    params.get('tab') === 'contacts' ||
    params.get('tab') === 'entitlements'
      ? (params.get('tab') as Tab)
      : 'overview';
  const siteQuery = params.get('siteQuery') ?? '';
  // Cards, or one table like the customer site status sheets (#8261).
  const siteView: 'cards' | 'table' = params.get('view') === 'table' ? 'table' : 'cards';
  const requestedGeneration = params.get('generation');
  const generationFilter: SiteGeneration | '' = siteGenerations.includes(
    requestedGeneration as SiteGeneration,
  )
    ? (requestedGeneration as SiteGeneration)
    : '';
  const [editing, setEditing] = useState(false);
  const [startingNewTerm, setStartingNewTerm] = useState(false);

  function setActiveTab(tab: Tab): void {
    const next = new URLSearchParams(params);
    if (tab === 'overview') next.delete('tab');
    else next.set('tab', tab);
    setParams(next, { replace: true });
  }

  function setSiteView(view: 'cards' | 'table'): void {
    const next = new URLSearchParams(params);
    if (view === 'table') next.set('view', 'table');
    else next.delete('view');
    setParams(next, { replace: true });
  }

  function setSiteFilter(name: 'siteQuery' | 'generation', value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next, { replace: true });
  }

  // Reuses the ['customers'] query already populated by CustomersPage — this
  // app has no dedicated GetCustomer-by-id call, and the full list is small
  // (~30 in production).
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
  });
  const customer = customers.data?.find((candidate) => candidate.id === customerId);

  // Scoped to this one customerId — exercises the real, working part of the
  // ListSites contract (customer_id is Required) rather than the unscoped
  // call that currently hides the global Sites nav entry. See
  // docs/decisions/ADR-0007-customer-entitlements-workspace.md.
  const sites = useQuery({
    queryKey: ['sites', customerId],
    queryFn: () => inventoryApi.listSites(customerId),
    enabled: Boolean(customerId),
  });

  const items = useQuery({
    queryKey: ['items-for-customer', customerId],
    queryFn: () => inventoryApi.listItems({ customerId, pageSize: 5000 }),
    enabled: Boolean(customerId) && activeTab !== 'entitlements',
  });

  const parts = useQuery({
    queryKey: ['parts'],
    queryFn: () => inventoryApi.listParts(),
    enabled: Boolean(customerId) && activeTab !== 'entitlements',
  });

  const insights = useMemo(
    () => buildCustomerInsights(sites.data ?? [], items.data?.data ?? [], parts.data ?? []),
    [items.data, parts.data, sites.data],
  );
  const location = useMemo(
    () => customerLocationSummary(customerId, sites.data ?? []),
    [customerId, sites.data],
  );

  // Biggest sites first: most active camera systems, then assigned, then
  // ordered.
  const sortedSiteInsights = useMemo(
    () =>
      [...insights.sites].sort(
        (left, right) =>
          compareCameraSystems(left.cameraSystems, right.cameraSystems) ||
          siteLabel(left.site).localeCompare(siteLabel(right.site)),
      ),
    [insights.sites],
  );

  const visibleSiteInsights = useMemo(() => {
    const needle = siteQuery.trim().toLowerCase();
    return sortedSiteInsights.filter(
      (insight) =>
        (!generationFilter || insight.generation === generationFilter) &&
        (!needle ||
          `${siteLabel(insight.site)} ${insight.site.id} ${insight.site.officialSiteId ?? ''} ${insight.site.labels?.country ?? ''} ${insight.site.labels?.region ?? ''} ${insight.site.region ?? ''} ${siteContactSearchText(insight.site)}`
            .toLowerCase()
            .includes(needle)),
    );
  }, [generationFilter, siteQuery, sortedSiteInsights]);
  const groupedSiteInsights = useMemo(
    () => groupCustomerSiteInsights(visibleSiteInsights),
    [visibleSiteInsights],
  );

  const generationOptions = useMemo<SearchableSelectOption[]>(() => {
    return [
      { value: '', label: 'All generations' },
      ...siteGenerations.map((generation) => ({
        value: generation,
        label: siteGenerationLabels[generation],
      })),
    ];
  }, []);

  const generationOverviewSites = useMemo(
    () =>
      [...insights.sites]
        .filter((insight) => !generationFilter || insight.generation === generationFilter)
        .sort(
          (left, right) =>
            SITE_GENERATION_ORDER[left.generation] - SITE_GENERATION_ORDER[right.generation] ||
            siteLabel(left.site).localeCompare(siteLabel(right.site)),
        )
        .slice(0, 8),
    [generationFilter, insights.sites],
  );

  const allocationOverviewSites = useMemo(
    () =>
      [...insights.sites].sort(
        (left, right) =>
          right.assignedItems - left.assignedItems ||
          siteLabel(left.site).localeCompare(siteLabel(right.site)),
      ),
    [insights.sites],
  );

  const generationSiteTotal =
    Object.values(insights.generationCounts).reduce((sum, count) => sum + count, 0) || 1;

  const subscription = useQuery({
    queryKey: ['customer-subscription', customerId],
    queryFn: () => inventoryApi.getCustomerSubscription(customerId),
    enabled: Boolean(customerId) && activeTab === 'entitlements',
    retry: false,
  });

  if (customers.isLoading) return <PageLoadingState label="Loading customer" />;
  if (customers.isError) {
    return (
      <ErrorState
        title="Customer could not be loaded"
        rpcName="ListCustomers"
        onRetry={() => void customers.refetch()}
      />
    );
  }
  if (!customer) {
    return (
      <div className="empty-state">
        <strong>No customer found with ID {customerId}.</strong>
        <Link to="/customers">Back to Customers</Link>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        backLink={<Link to="/customers">Customers</Link>}
        title={customer.displayName ?? customer.name}
        description={
          <>
            {humanizeIdentifier(customerType(customer))}
            {location.regions.length > 0 ? ` · ${location.regions.join(', ')}` : ''} ·{' '}
            {humanizeIdentifier(customer.state)}
          </>
        }
      />

      <div className="detail-tabs" role="tablist" aria-label="Customer sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'overview'}
          className={activeTab === 'overview' ? 'active' : undefined}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'sites'}
          className={activeTab === 'sites' ? 'active' : undefined}
          onClick={() => setActiveTab('sites')}
        >
          Sites
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'contacts'}
          className={activeTab === 'contacts' ? 'active' : undefined}
          onClick={() => setActiveTab('contacts')}
        >
          Contacts
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'entitlements'}
          className={activeTab === 'entitlements' ? 'active' : undefined}
          onClick={() => setActiveTab('entitlements')}
        >
          Entitlements
        </button>
      </div>

      {activeTab === 'overview' ? (
        <>
          {sites.isLoading || items.isLoading || parts.isLoading ? (
            <PageLoadingState label="Loading customer overview" className="section-loading-state" />
          ) : null}
          {sites.isError || items.isError || parts.isError ? (
            <ErrorState
              title="Customer operations could not be loaded"
              rpcName="ListCustomerOperations"
              onRetry={() => {
                void sites.refetch();
                void items.refetch();
                void parts.refetch();
              }}
            />
          ) : null}
          {sites.isSuccess && items.isSuccess && parts.isSuccess ? (
            <>
              <div
                className="compact-metrics customer-overview-metrics"
                aria-label="Customer operations summary"
              >
                <Link
                  className="customer-metric-link"
                  to={`/customers/${encodeURIComponent(customer.id)}?tab=sites`}
                >
                  <span>Sites</span>
                  <strong>{insights.sites.length.toLocaleString()}</strong>
                </Link>
                <Link
                  className="customer-metric-link"
                  to={customerInventoryHref(customer.id, 'assigned')}
                >
                  <span>Assigned inventory</span>
                  <strong>{insights.assignedItems.toLocaleString()}</strong>
                </Link>
                <Link
                  className="customer-metric-link"
                  to={customerInventoryHref(customer.id, 'on-site')}
                >
                  <span>Currently at sites</span>
                  <strong>{insights.locatedItems.toLocaleString()}</strong>
                </Link>
                <Link
                  className={`customer-metric-link${insights.needsAttention > 0 ? ' metric-attention' : ''}`}
                  to={customerInventoryHref(customer.id, undefined, true)}
                >
                  <span>Needs attention</span>
                  <strong>{insights.needsAttention.toLocaleString()}</strong>
                </Link>
                <Link
                  className="customer-metric-link"
                  to={customerInventoryHref(customer.id, 'away-from-assignment')}
                >
                  <span>Away from assigned site</span>
                  <strong>{insights.locationVariance.toLocaleString()}</strong>
                </Link>
              </div>

              <section className="panel customer-generation-panel">
                <div className="panel-heading">
                  <div>
                    <h2>Site generation</h2>
                    <div className="panel-context">
                      Select a generation to filter assigned hardware by site
                    </div>
                  </div>
                  <div className="panel-heading-actions">
                    {generationFilter ? (
                      <button
                        type="button"
                        className="quiet-button"
                        onClick={() => setSiteFilter('generation', '')}
                      >
                        Clear filter
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="quiet-button"
                      onClick={() => setActiveTab('sites')}
                    >
                      View all sites
                    </button>
                  </div>
                </div>

                {insights.sites.length > 0 ? (
                  <>
                    <div
                      className={`family-bar generation-family-bar${generationFilter ? ' is-filtered' : ''}`}
                      role="group"
                      aria-label="Site distribution by hardware generation"
                    >
                      {siteGenerations.map((generation) => {
                        const count = insights.generationCounts[generation];
                        if (count === 0) return null;
                        const percent = (count / generationSiteTotal) * 100;
                        const selected = generationFilter === generation;
                        return (
                          <button
                            key={generation}
                            type="button"
                            className={`family-bar-segment generation-bar-segment generation-${generation}`}
                            style={{
                              width: `${Math.max(percent, 1)}%`,
                              background: 'var(--generation-color)',
                            }}
                            aria-label={`Show ${siteGenerationLabels[generation]} sites from generation bar`}
                            aria-pressed={selected}
                            title={`${siteGenerationLabels[generation]}: ${count.toLocaleString()} site${count === 1 ? '' : 's'}`}
                            onClick={() => setSiteFilter('generation', selected ? '' : generation)}
                          />
                        );
                      })}
                    </div>
                    <ul
                      className={`family-legend generation-legend${generationFilter ? ' is-filtered' : ''}`}
                      aria-label="Filter sites by hardware generation"
                    >
                      {siteGenerations.map((generation) => {
                        const count = insights.generationCounts[generation];
                        const selected = generationFilter === generation;
                        return (
                          <li key={generation} className={`generation-${generation}`}>
                            <button
                              type="button"
                              className="generation-legend-button"
                              aria-label={`Filter ${siteGenerationLabels[generation]} sites (${count.toLocaleString()})`}
                              aria-pressed={selected}
                              disabled={count === 0}
                              onClick={() =>
                                setSiteFilter('generation', selected ? '' : generation)
                              }
                            >
                              <span
                                className="family-swatch"
                                style={{ background: 'var(--generation-color)' }}
                                aria-hidden="true"
                              />
                              {siteGenerationLabels[generation]}
                              <span className="mono family-legend-count">
                                {count.toLocaleString()}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>

                    {generationOverviewSites.length > 0 ? (
                      <div className="customer-generation-table">
                        <div
                          className="customer-generation-row customer-generation-head"
                          aria-hidden="true"
                        >
                          <span>Site</span>
                          <span>Generation</span>
                          <span>Gen 1 hardware</span>
                          <span>Gen 2 hardware</span>
                          <span>Assigned</span>
                        </div>
                        {generationOverviewSites.map((insight) => (
                          <Link
                            className="customer-generation-row"
                            key={insight.site.id}
                            to={`/inventory?assignedSiteId=${encodeURIComponent(insight.site.id)}`}
                          >
                            <span>{siteLabel(insight.site)}</span>
                            <span>
                              <span className={`generation-badge generation-${insight.generation}`}>
                                {siteGenerationLabels[insight.generation]}
                              </span>
                            </span>
                            <span>{componentSummary('gen1', insight.gen1Components)}</span>
                            <span>{componentSummary('gen2', insight.gen2Components)}</span>
                            <span>{insight.assignedItems.toLocaleString()}</span>
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <p className="empty-note">No sites match this generation.</p>
                    )}
                  </>
                ) : (
                  <p className="empty-note">No sites recorded for this customer.</p>
                )}
              </section>

              <div className="dashboard-detail-grid customer-overview-secondary">
                <section className="panel customer-allocation-panel">
                  <h2>Site allocation</h2>
                  {allocationOverviewSites.length > 0 ? (
                    <div className="customer-allocation-list">
                      {allocationOverviewSites.slice(0, 5).map((insight) => (
                        <Link
                          to={`/inventory?assignedSiteId=${encodeURIComponent(insight.site.id)}`}
                          key={insight.site.id}
                        >
                          <span>{siteLabel(insight.site)}</span>
                          <strong>{insight.assignedItems.toLocaleString()}</strong>
                          <span className="customer-allocation-track" aria-hidden="true">
                            <span
                              style={{
                                width: `${insights.assignedItems > 0 ? (insight.assignedItems / insights.assignedItems) * 100 : 0}%`,
                              }}
                            />
                          </span>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="empty-note">No accountable inventory recorded.</p>
                  )}
                </section>

                <div className="dashboard-section-divider" aria-hidden="true" />

                <section className="panel customer-profile-panel">
                  <h2>Account profile</h2>
                  <dl>
                    <dt>Type</dt>
                    <dd>{humanizeIdentifier(customerType(customer))}</dd>
                    <dt>State</dt>
                    <dd>{humanizeIdentifier(customer.state)}</dd>
                    <dt>Region</dt>
                    <dd>
                      {location.regions.length > 0 ? location.regions.join(', ') : 'Not recorded'}
                    </dd>
                    <dt>Country</dt>
                    <dd>
                      {location.countries.length > 0
                        ? location.countries.join(', ')
                        : 'Not recorded'}
                    </dd>
                    <dt>Inventory ID</dt>
                    <dd className="mono">{customer.id}</dd>
                  </dl>
                </section>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {activeTab === 'contacts' ? (
        <CustomerContactsTab
          customerId={customer.id}
          customerName={customer.displayName ?? customer.name}
          sites={sites.data ?? []}
        />
      ) : null}

      {activeTab === 'sites' ? (
        <section className="panel">
          <div className="panel-heading">
            <div>
              <h2>Sites</h2>
            </div>
            {sites.isSuccess && items.isSuccess && parts.isSuccess ? (
              <span className="panel-context">
                {visibleSiteInsights.length.toLocaleString()} sites
              </span>
            ) : null}
          </div>
          {sites.isLoading || items.isLoading || parts.isLoading ? (
            <PageLoadingState label="Loading sites" className="section-loading-state" />
          ) : null}
          {sites.isError || items.isError || parts.isError ? (
            <ErrorState
              title="Sites could not be loaded"
              rpcName="ListCustomerSites"
              onRetry={() => {
                void sites.refetch();
                void items.refetch();
                void parts.refetch();
              }}
            />
          ) : null}
          {sites.isSuccess && sites.data.length === 0 ? (
            <p className="empty-note">No sites recorded for this customer.</p>
          ) : null}
          {sites.isSuccess && items.isSuccess && parts.isSuccess && insights.sites.length > 0 ? (
            <>
              <form
                className="customer-site-controls"
                role="search"
                aria-label="Filter customer sites"
              >
                <label>
                  <span>Search</span>
                  <input
                    type="search"
                    value={siteQuery}
                    onChange={(event) => setSiteFilter('siteQuery', event.target.value)}
                    placeholder="Site name, code, ID, region, contact, or address"
                    aria-label="Search customer sites"
                  />
                </label>
                <label>
                  <span>Generation</span>
                  <SearchableSelect
                    ariaLabel="Site generation"
                    value={generationFilter}
                    onChange={(value) => setSiteFilter('generation', value)}
                    options={generationOptions}
                    placeholder="All generations"
                  />
                </label>
              </form>
              <div className="customer-site-view" role="group" aria-label="Show sites as">
                {(['cards', 'table'] as const).map((view) => (
                  <button
                    type="button"
                    key={view}
                    className={siteView === view ? 'primary' : undefined}
                    aria-pressed={siteView === view}
                    onClick={() => setSiteView(view)}
                  >
                    {view === 'cards' ? 'Cards' : 'Table'}
                  </button>
                ))}
              </div>

              {visibleSiteInsights.length > 0 && siteView === 'table' ? (
                <SiteStatusTable
                  groups={groupedSiteInsights}
                  statusBySite={siteStatus.bySite}
                  pensBySite={penCounts.bySite}
                  openRequestsBySite={openRequestsBySite}
                  expandAll={Boolean(siteQuery.trim() || generationFilter)}
                  exportName={customer.displayName ?? customer.name}
                  key={`${siteQuery.trim()}|${generationFilter}`}
                />
              ) : visibleSiteInsights.length > 0 ? (
                <div className="customer-site-geography">
                  {groupedSiteInsights.map((country) => (
                    <details className="customer-site-country" key={country.key} open>
                      <summary>
                        <h3>{country.label}</h3>
                        <span className="customer-site-group-count">
                          {country.regions.reduce(
                            (count, region) => count + region.sites.length,
                            0,
                          )}{' '}
                          sites
                        </span>
                      </summary>
                      <div className="customer-site-country-content">
                        {country.regions.map((region) => (
                          <details className="customer-site-region" key={region.key} open>
                            <summary>
                              <h4>{region.label}</h4>
                              <span className="customer-site-group-count">
                                {region.sites.length} sites
                              </span>
                            </summary>
                            <div className="customer-site-grid">
                              {region.sites.map((insight) => (
                                <CustomerSiteCard
                                  insight={insight}
                                  openRequests={openRequestsBySite.get(insight.site.id) ?? 0}
                                  status={siteStatus.bySite.get(insight.site.id)}
                                  pens={penCounts.bySite.get(insight.site.id)}
                                  key={insight.site.id}
                                />
                              ))}
                            </div>
                          </details>
                        ))}
                      </div>
                    </details>
                  ))}
                </div>
              ) : (
                <div className="empty-state customer-sites-empty">
                  <strong>No sites match these filters.</strong>
                  <button
                    type="button"
                    onClick={() => {
                      const next = new URLSearchParams(params);
                      next.delete('siteQuery');
                      next.delete('generation');
                      setParams(next, { replace: true });
                    }}
                  >
                    Clear filters
                  </button>
                </div>
              )}
            </>
          ) : null}
        </section>
      ) : null}

      {activeTab === 'entitlements' ? (
        <>
          {subscription.isLoading ? <p role="status">Loading entitlements…</p> : null}

          {subscription.error instanceof ForbiddenError ? (
            <div className="empty-state">
              <strong>Admin access required</strong>
              <p>
                Sign in with a <span className="mono">@tidalx.ai</span> account to view
                entitlements.
              </p>
            </div>
          ) : null}

          {subscription.error instanceof NotFoundError ? (
            <div className="empty-state">
              <strong>No entitlement record found for this customer.</strong>
            </div>
          ) : null}

          {subscription.isError &&
          !(subscription.error instanceof ForbiddenError) &&
          !(subscription.error instanceof NotFoundError) ? (
            <ErrorState
              title="Entitlements could not be loaded"
              rpcName="GetCustomerSubscription"
              onRetry={() => void subscription.refetch()}
            />
          ) : null}

          {subscription.data ? (
            <>
              <section className="panel">
                <div className="panel-heading">
                  <div>
                    <h2>Entitlements</h2>
                    <div className="panel-context">
                      Last updated {formatDate(subscription.data.updateTime)}
                    </div>
                  </div>
                  <div className="panel-actions">
                    <button
                      type="button"
                      onClick={() => setEditing(true)}
                      disabled={!canMutate}
                      title={canMutate ? 'Edit this term' : 'Sign in to edit entitlements'}
                    >
                      Edit this term
                    </button>
                    <button
                      type="button"
                      onClick={() => setStartingNewTerm(true)}
                      disabled={!canMutate}
                      title={
                        canMutate ? 'Start a new contract term' : 'Sign in to edit entitlements'
                      }
                    >
                      Start new contract term
                    </button>
                  </div>
                </div>

                <div className="term-strip">
                  <span className="tier-chip">{subscription.data.tier ?? 'BASIC'}</span>
                  <div className="term-dates">
                    <span className="mono">{formatDate(subscription.data.startTime)}</span>
                    <div className="term-bar">
                      <span
                        style={{
                          width: `${Math.round(termProgress(subscription.data.startTime, subscription.data.endTime) * 100)}%`,
                        }}
                      />
                    </div>
                    <span className="mono">{formatDate(subscription.data.endTime)}</span>
                  </div>
                  <span
                    className={`status-chip status-chip-${
                      subscriptionTermStatus(subscription.data.endTime) === 'active'
                        ? 'ok'
                        : subscriptionTermStatus(subscription.data.endTime) === 'renewing-soon'
                          ? 'warn'
                          : 'due'
                    }`}
                  >
                    {TERM_STATUS_LABELS[subscriptionTermStatus(subscription.data.endTime)]}
                  </span>
                  <span className="revision-tag">revision {subscription.data.revision}</span>
                </div>

                <p className="feature-grid-heading">Feature access</p>
                <div className="feature-grid">
                  {REAL_FEATURES.map((feature) => (
                    <span
                      className={`feature-chip ${subscription.data.features.includes(feature) ? 'on' : 'off'}`}
                      key={feature}
                    >
                      {humanizeIdentifier(feature)}
                    </span>
                  ))}
                </div>

                <dl className="detail-definition-grid">
                  <dt>Contract</dt>
                  <dd>
                    {subscription.data.contractUrl ? (
                      <a href={subscription.data.contractUrl} target="_blank" rel="noreferrer">
                        {subscription.data.contractName || 'View contract'}
                      </a>
                    ) : (
                      subscription.data.contractName || 'Not recorded'
                    )}
                  </dd>
                  <dt>Entitled systems</dt>
                  <dd className="mono">{subscription.data.numberOfSystems ?? '—'}</dd>
                  <dt>Regions</dt>
                  <dd>
                    {subscription.data.regions && subscription.data.regions.length > 0
                      ? subscription.data.regions.map((region) => (
                          <span className="region-chip" key={region}>
                            {region}
                          </span>
                        ))
                      : 'Not recorded'}
                  </dd>
                  <dt className="detail-span-3">Notes</dt>
                  <dd className="detail-span-3">
                    {subscription.data.notes || 'No notes recorded.'}
                  </dd>
                </dl>
              </section>

              {editing ? (
                <EditSubscriptionDrawer
                  customerId={customerId}
                  subscription={subscription.data}
                  onClose={() => setEditing(false)}
                />
              ) : null}
              {startingNewTerm ? (
                <NewContractTermDialog
                  customerId={customerId}
                  currentSubscription={subscription.data}
                  onClose={() => setStartingNewTerm(false)}
                />
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </>
  );
}
